import React, { useCallback, useContext, useMemo, useState } from "react";
import { CompetitionContext } from "contexts/CompetitionContext";
import { BodyContainer } from "App/styles";
import ListOfEntries from "components/ListOfEntries";
import { MainTitle } from "components/MainTitle";
import { InputField, SelectField } from "basics";
import { Button, CleanButton } from "basics/buttons";
import useEntries from "hooks/useEntries";
import Confirmation from "components/Confirmation";
import EntryWorkflow from "components/EntryWorkflow";
import { entryMatchesSearch, normalizeText } from "helpers";
import { WorkflowStep } from "model/dictionary";
import {
  ContainerAnimated,
  EntriesFilter,
  Filter,
  PageDescription,
  StageNotice,
} from "./styles";

import AnimatedControl from "components/AnimatedControl";

// Qué se deja de poder hacer en cada paso fuera de su ventana de fechas.
const stepAction = {
  [WorkflowStep.REVIEW]: {
    name: "La preselección",
    action: "valorar las postulaciones",
  },
  [WorkflowStep.FINALISTS]: {
    name: "La elección de finalistas",
    action: "elegir finalistas",
  },
  [WorkflowStep.VOTING]: {
    name: "La votación",
    action: "votar",
  },
  [WorkflowStep.WINNERS]: {
    name: "La elección de ganadores",
    action: "elegir ganadores",
  },
};

const dayMonth = (time) =>
  new Intl.DateTimeFormat("es-AR", { day: "numeric", month: "long" }).format(
    new Date(time)
  );

// Fuera de la ventana el listado queda a la vista, pero sin las acciones del
// paso: este texto explica por qué y desde (o hasta) cuándo.
const closedStepMessage = (step, status, range) => {
  const { name, action } = stepAction[step];

  if (status === "before") {
    return `${name} abre el ${dayMonth(
      range.start
    )}. Hasta entonces las postulaciones se pueden consultar, pero no se puede ${action}.`;
  }

  return `${name} cerró el ${dayMonth(
    range.end
  )}. Las postulaciones se pueden consultar, pero ya no se puede ${action}. Si hace falta reabrirla, se corrigen las fechas en Administración > Competiciones.`;
};

export default function EntriesPage({
  title,
  description,
  workflowStep,
  searchable = false,
  entryListMode,
  formStatusFiltered,
  formStatusConfirmed,
  selectionMode,
}) {
  const {
    entries,
    awards,
    awardSelected,
    handleFilter,
    resetFilter,
    loading,
    emptyMessage,
    onSelected,
    onCleanSelected,
    isSelected,
    confirmChangeStatus,
    areEntriesSelected,
  } = useEntries({
    mode: entryListMode,
    formStatus: formStatusFiltered,
    selectionMode: selectionMode,
  });

  const [formOpened, setFormOpened] = useState(false);

  // Cada paso sólo se puede hacer dentro de sus fechas (ver `getStepWindows`
  // en `CompetitionContext`). Afuera, la pantalla queda de sólo lectura.
  const { stepStatus, stepWindows } = useContext(CompetitionContext);
  const status = stepStatus[workflowStep];
  const readOnly = Boolean(status && status !== "open");

  // Búsqueda en memoria sobre las postulaciones del premio ya traídas, igual
  // que en el listado de administración. `InputField` no es controlado:
  // "Limpiar" lo vacía remontándolo con otra `key`.
  const [search, setSearch] = useState("");
  const [searchKey, setSearchKey] = useState(0);

  const visibleEntries = useMemo(
    () =>
      search
        ? entries.filter((entry) => entryMatchesSearch(entry, search))
        : entries,
    [entries, search]
  );

  const onSearch = (field, value) => {
    setSearch(normalizeText(value));
  };

  const onReset = () => {
    setSearch("");
    setSearchKey((prev) => prev + 1);

    resetFilter();
  };

  const FilterContainer = searchable ? EntriesFilter : Filter;

  const onConfirmChangeStatus = (newStatus) => {
    setFormOpened(false);

    confirmChangeStatus(newStatus);
  };

  const onOpenForm = () => {
    setFormOpened(true);
  };

  const onCloseForm = useCallback(async () => {
    if (!areEntriesSelected()) {
      setFormOpened(false);
    }
  }, [areEntriesSelected]);

  const onCleanOpenForm = () => {
    setFormOpened(false);
  };

  return (
    <BodyContainer>
      <MainTitle title={title} seoTitle={title} />

      <EntryWorkflow current={workflowStep} />

      {description && <PageDescription>{description}</PageDescription>}

      {readOnly && (
        <StageNotice role="status">
          {closedStepMessage(workflowStep, status, stepWindows[workflowStep])}
        </StageNotice>
      )}

      <FilterContainer>
        {searchable && (
          <InputField
            key={searchKey}
            id="search"
            label="Buscar"
            placeholder="Proyecto, nombre, mail o teléfono"
            onChange={onSearch}
          />
        )}
        <SelectField
          id="award.id"
          value={awardSelected}
          options={awards}
          onChange={handleFilter}
          label="Premios"
          defaultOption="Elija un premio"
        />

        <CleanButton onClick={onReset}>Limpiar</CleanButton>
        {formStatusConfirmed && !readOnly && (
          <Button
            disabled={!areEntriesSelected()}
            onClick={() => {
              onOpenForm();
            }}
          >
            Confirmar
          </Button>
        )}
      </FilterContainer>

      <ContainerAnimated>
        <AnimatedControl
          condition={formOpened && onCloseForm()}
          control={
            <Confirmation
              type="confirm"
              text="¿Confirma la selección?"
              onClick={onConfirmChangeStatus}
              paramOk={formStatusConfirmed}
              cancel={onCleanOpenForm}
            />
          }
        />
      </ContainerAnimated>

      <ListOfEntries
        entries={visibleEntries}
        mode={entryListMode}
        readOnly={readOnly}
        loading={loading}
        emptyMessage={
          search && entries.length > 0
            ? "No hay postulaciones que coincidan con la búsqueda."
            : emptyMessage
        }
        onSelected={onSelected}
        onCleanSelected={onCleanSelected}
        isSelected={isSelected}
      />
    </BodyContainer>
  );
}
